import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";
import type { POSConfig, TaxRule } from "../../types";
import "../../styles/settings.css";

const DEFAULT_CONFIG: POSConfig = {
  organizationId: "",
  taxRules: [
    { id: "state", name: "State Sales Tax", rate: 0.07, type: "state", enabled: true },
  ],
  paymentMethods: { cash: true, card: true, giftCard: false, other: false },
  receiptSettings: { showLogo: true, headerText: "", footerText: "Thank you for your purchase!", autoPrint: false },
  terminalSettings: { requireLocation: true, allowDiscounts: false, allowVoid: true, defaultTaxEnabled: true },
};

export default function POSSettings({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [config, setConfig] = useState<POSConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // New tax rule form
  const [newTax, setNewTax] = useState({ name: "", rate: "", type: "local" as TaxRule["type"] });

  useEffect(() => { if (orgId) loadConfig(); }, [orgId]);

  async function loadConfig() {
    try {
      const data = await apiGet<any>(`/organizations/${orgId}/pos-config`);
      if (data && data.taxRules) {
        setConfig(data);
      } else {
        setConfig({ ...DEFAULT_CONFIG, organizationId: orgId! });
      }
    } catch {
      setConfig({ ...DEFAULT_CONFIG, organizationId: orgId! });
    } finally {
      setLoading(false);
    }
  }

  const save = async (updated: POSConfig) => {
    setStatus(null);
    setSaving(true);
    try {
      await apiPut(`/organizations/${orgId}/pos-config`, updated);
      setConfig(updated);
      setStatus({ type: "success", message: "POS settings saved" });
    } catch (err) {
      setStatus({ type: "error", message: err instanceof Error ? err.message : "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const toggleTax = (id: string) => {
    const updated = { ...config, taxRules: config.taxRules.map((t) => t.id === id ? { ...t, enabled: !t.enabled } : t) };
    save(updated);
  };

  const removeTax = (id: string) => {
    const updated = { ...config, taxRules: config.taxRules.filter((t) => t.id !== id) };
    save(updated);
  };

  const addTax = () => {
    if (!newTax.name || !newTax.rate) return;
    const rule: TaxRule = { id: crypto.randomUUID(), name: newTax.name, rate: parseFloat(newTax.rate) / 100, type: newTax.type, enabled: true };
    const updated = { ...config, taxRules: [...config.taxRules, rule] };
    save(updated);
    setNewTax({ name: "", rate: "", type: "local" });
  };

  const togglePayment = (key: keyof POSConfig["paymentMethods"]) => {
    const updated = { ...config, paymentMethods: { ...config.paymentMethods, [key]: !config.paymentMethods[key] } };
    save(updated);
  };

  const toggleTerminal = (key: keyof POSConfig["terminalSettings"]) => {
    const updated = { ...config, terminalSettings: { ...config.terminalSettings, [key]: !config.terminalSettings[key] } };
    save(updated);
  };

  const updateReceipt = (key: keyof POSConfig["receiptSettings"], value: string | boolean) => {
    const updated = { ...config, receiptSettings: { ...config.receiptSettings, [key]: value } };
    setConfig(updated);
  };

  const saveReceipt = () => save(config);

  if (loading) return <div className="modal-overlay"><div className="modal"><p>Loading POS settings...</p></div></div>;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h3 style={{ margin: 0 }}>⚙️ POS Settings</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" }}>✕</button>
        </div>

        {status && <div style={{ marginBottom: "1rem" }}><span className={`save-status ${status.type}`}>{status.message}</span></div>}

        {/* Tax Rules */}
        <div className="settings-section" style={{ marginBottom: "1.5rem" }}>
          <h2>Tax Rules</h2>
          <p className="section-desc">Configure state, local, and special tax rates</p>

          {config.taxRules.map((tax) => (
            <div key={tax.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid #f3f4f6" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, cursor: "pointer" }}>
                <input type="checkbox" checked={tax.enabled} onChange={() => toggleTax(tax.id)} />
                <span style={{ fontWeight: 500 }}>{tax.name}</span>
                <span style={{ color: "#6b7280", fontSize: "0.85rem" }}>({(tax.rate * 100).toFixed(2)}% — {tax.type})</span>
              </label>
              <button onClick={() => removeTax(tax.id)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "0.85rem" }}>Remove</button>
            </div>
          ))}

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.2rem" }}>Name</label>
              <input value={newTax.name} onChange={(e) => setNewTax((f) => ({ ...f, name: e.target.value }))} placeholder="County Tax" style={{ width: "100%", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85rem" }} />
            </div>
            <div style={{ width: "80px" }}>
              <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.2rem" }}>Rate %</label>
              <input type="number" step="0.01" value={newTax.rate} onChange={(e) => setNewTax((f) => ({ ...f, rate: e.target.value }))} placeholder="1.5" style={{ width: "100%", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85rem" }} />
            </div>
            <div style={{ width: "100px" }}>
              <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.2rem" }}>Type</label>
              <select value={newTax.type} onChange={(e) => setNewTax((f) => ({ ...f, type: e.target.value as any }))} style={{ width: "100%", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85rem" }}>
                <option value="state">State</option>
                <option value="local">Local</option>
                <option value="county">County</option>
                <option value="special">Special</option>
              </select>
            </div>
            <button onClick={addTax} className="btn btn-primary" style={{ width: "auto", height: "36px", fontSize: "0.85rem" }}>Add</button>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="settings-section" style={{ marginBottom: "1.5rem" }}>
          <h2>Payment Methods</h2>
          <p className="section-desc">Enable or disable payment options at checkout</p>

          {(["cash", "card", "giftCard", "other"] as const).map((key) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0", cursor: "pointer" }}>
              <input type="checkbox" checked={config.paymentMethods[key]} onChange={() => togglePayment(key)} />
              <span style={{ textTransform: "capitalize" }}>{key === "giftCard" ? "Gift Card" : key}</span>
            </label>
          ))}
        </div>

        {/* Terminal Settings */}
        <div className="settings-section" style={{ marginBottom: "1.5rem" }}>
          <h2>Terminal Settings</h2>
          <p className="section-desc">General POS behavior</p>

          {([
            ["requireLocation", "Require location selection before checkout"],
            ["allowDiscounts", "Allow manual discounts"],
            ["allowVoid", "Allow voiding transactions"],
            ["defaultTaxEnabled", "Apply tax by default on taxable items"],
          ] as const).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0", cursor: "pointer" }}>
              <input type="checkbox" checked={config.terminalSettings[key]} onChange={() => toggleTerminal(key)} />
              <span>{label}</span>
            </label>
          ))}
        </div>

        {/* Receipt Settings */}
        <div className="settings-section">
          <h2>Receipt Settings</h2>
          <p className="section-desc">Customize printed and digital receipts</p>

          <div className="settings-form">
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", marginBottom: "0.75rem" }}>
              <input type="checkbox" checked={config.receiptSettings.autoPrint} onChange={(e) => updateReceipt("autoPrint", e.target.checked)} />
              <span>Auto-print receipt after checkout</span>
            </label>
            <div className="form-group">
              <label>Receipt Header Text</label>
              <input value={config.receiptSettings.headerText} onChange={(e) => updateReceipt("headerText", e.target.value)} placeholder="Store Name or Tagline" />
            </div>
            <div className="form-group">
              <label>Receipt Footer Text</label>
              <input value={config.receiptSettings.footerText} onChange={(e) => updateReceipt("footerText", e.target.value)} placeholder="Thank you for your purchase!" />
            </div>
            <button onClick={saveReceipt} className="btn btn-primary" style={{ width: "auto" }} disabled={saving}>
              {saving ? "Saving..." : "Save Receipt Settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
